import { useEffect, useState } from "react";
import { BellRing, Loader2, TrendingUp, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ReferralLeaderboard } from "@/components/admin/ReferralLeaderboard";

function Stat({ label, value, sub, testid }) {
  return (
    <div className="dg-card rounded-xl p-4" data-testid={testid}>
      <div className="dg-eyebrow">{label}</div>
      <div className="font-display font-extrabold text-2xl mt-1 text-white">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function Trend({ trend }) {
  const max = Math.max(1, ...trend.map((t) => t.mrr));
  return (
    <div className="dg-card rounded-xl p-4" data-testid="member-mrr-trend">
      <div className="dg-eyebrow flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Monthly revenue · last 6 months</div>
      <div className="mt-3 flex items-end gap-2 h-24">
        {trend.map((t) => (
          <div key={t.month} className="flex-1 flex flex-col items-center gap-1" title={`${t.label}: $${t.mrr.toFixed(0)} · ${t.members} member${t.members === 1 ? "" : "s"}`} data-testid={`mrr-bar-${t.month}`}>
            <div className="text-[10px] text-slate-400">${t.mrr.toFixed(0)}</div>
            <div className="w-full rounded-t bg-[#FFD600]/80 transition-[height]" style={{ height: `${Math.max(3, (t.mrr / max) * 64)}px` }} />
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{t.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MemberInsights({ onNudged }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const [digest, setDigest] = useState(false);
  const load = () => api.get("/admin/memberships/insights").then((r) => setD(r.data)).catch(() => setD(false));
  useEffect(() => { load(); }, []);

  const sendDigest = async () => {
    setDigest(true);
    try { const r = await api.post("/admin/memberships/digest-preview"); toast.success(`Digest sent to ${r.data.to}`); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't send the digest"); }
    finally { setDigest(false); }
  };

  const nudge = async (m) => {
    setBusy(m.membership_id);
    try { const r = await api.post(`/admin/memberships/${m.membership_id}/nudge`); toast.success(`Nudge sent to ${r.data.to}`); load(); onNudged?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't send the nudge"); }
    finally { setBusy(""); }
  };

  if (d === null) return <div className="text-slate-500 text-sm">Loading insights…</div>;
  if (!d) return null;
  const care = d.by_tier.care, plus = d.by_tier.care_plus;
  return (
    <div className="space-y-4" data-testid="member-insights">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-slate-500">A digest like this lands in your inbox every Monday at 8 AM Pacific.</span>
        <button type="button" onClick={sendDigest} disabled={digest} className="dg-btn-ghost rounded-full px-3 py-1.5 text-xs flex items-center gap-1 ml-auto disabled:opacity-50" data-testid="digest-preview-btn">
          {digest ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Email me this week's digest now
        </button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat testid="insight-mrr" label="Monthly revenue" value={`$${d.mrr.toFixed(2)}`} sub={`${care.members} Care · ${plus.members} Care+`} />
        <Stat testid="insight-active" label="Active members" value={d.counts.active} sub={`${d.counts.cancelling} cancelling · ${d.counts.pending} pending · ${d.counts.cancelled} cancelled${d.counts.problem ? ` · ${d.counts.problem} billing problem` : ""}`} />
        <Stat testid="insight-included" label="Included sessions this month" value={`${d.used_included} used · ${d.unused_included.length} waiting`} sub="Care+ 30-minute sessions" />
        <Stat testid="insight-savings" label="Members saved this month" value={`$${d.member_savings_month.toFixed(2)}`} sub={`${d.member_tickets_month} priority ticket${d.member_tickets_month === 1 ? "" : "s"} this month`} />
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        <Trend trend={d.trend} />
        <div className="dg-card rounded-xl p-4" data-testid="member-unused-list">
          <div className="dg-eyebrow flex items-center gap-1"><BellRing className="w-3 h-3" /> Care+ members who haven't used this month's session</div>
          {d.unused_included.length === 0
            ? <div className="text-sm text-slate-500 mt-3" data-testid="member-unused-empty">Everyone's used their included session — or there are no Care+ members yet.</div>
            : <ul className="mt-3 space-y-2 text-sm">
              {d.unused_included.map((m) => (
                <li key={m.membership_id} className="flex flex-wrap items-center gap-2" data-testid={`unused-row-${m.membership_id}`}>
                  <span className="font-medium">{m.name}</span>
                  <a href={`mailto:${m.email}`} className="dg-link text-xs">{m.email}</a>
                  {m.nudged_this_month && <span className="dg-chip text-xs" data-testid={`nudged-chip-${m.membership_id}`}>Nudged {new Date(m.last_nudge_at).toLocaleDateString()}</span>}
                  <button type="button" onClick={() => nudge(m)} disabled={busy === m.membership_id} className="dg-btn-ghost rounded-full px-3 py-1 text-xs ml-auto flex items-center gap-1 disabled:opacity-50" data-testid={`nudge-btn-${m.membership_id}`}>
                    {busy === m.membership_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellRing className="w-3 h-3" />} {m.nudged_this_month ? "Nudge again" : "Send nudge"}
                  </button>
                </li>
              ))}
            </ul>}
          <p className="text-xs text-slate-500 mt-3">One tap emails a friendly "your included session is still waiting" note with a Book button. Sessions don't roll over — use the nudge mid-month.</p>
        </div>
      </div>
      <ReferralLeaderboard d={d} onChange={load} />
    </div>
  );
}
